import React, { useState, useEffect } from 'react';
import { AlertCircle, Lock, Eye, EyeOff, CheckCircle, Loader } from 'lucide-react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';

// Translate the raw fastapi-users error codes from /auth/reset-password into
// human-friendly copy. `detail` is a bare string code for a bad token, or an
// object { code, reason } for a rejected password.
const friendlyResetError = (detail: any): string => {
  const code = typeof detail === 'object' && detail ? detail.code : detail;

  switch (code) {
    case 'RESET_PASSWORD_BAD_TOKEN':
      return 'This password reset link is invalid or has expired. Please request a new one.';
    case 'RESET_PASSWORD_INVALID_PASSWORD':
      // fastapi-users includes a human-readable reason for weak passwords.
      return (typeof detail === 'object' && detail?.reason)
        ? detail.reason
        : 'That password does not meet the requirements. Please choose a stronger one.';
    default:
      return typeof detail === 'string' && detail
        ? detail
        : 'Failed to reset password. Please try again.';
  }
};

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [resetToken, setResetToken] = useState('');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [isValidCode, setIsValidCode] = useState(false);

  useEffect(() => {
    // fastapi-users sends ?token=... in the reset link
    const token = searchParams.get('token');

    if (!token) {
      setError('Invalid or missing reset code. Please request a new password reset link.');
      setVerifying(false);
      return;
    }

    setResetToken(token);
    verifyCode(token);
  }, [searchParams]);

  const verifyCode = async (code: string) => {
    try {
      // For token-based reset, the code itself is valid if present
      // Actual validation happens when the password is reset
      setIsValidCode(true);
      setEmail(''); // Email will be resolved server-side
      console.log('Password reset code present, proceeding to reset form');
    } catch (error: any) {
      console.error('Error verifying reset code:', error);
      setError('Unable to verify reset code. Please try again or request a new link.');
    } finally {
      setVerifying(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!newPassword.trim()) {
      setError('Please enter a new password');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const apiBase = (typeof window !== 'undefined' && window.location.hostname === 'localhost')
        ? 'http://localhost:8000/api/'
        : (import.meta.env.VITE_API_URL || 'https://pawtected-client-dev-b941ac1d80e9.herokuapp.com/api/');

      const response = await fetch(`${apiBase}auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, password: newPassword })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(friendlyResetError(data.detail));
      }

      setSuccess(true);
      console.log('Password reset successful');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error: any) {
      console.error('Password reset error:', error);
      setError(error.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-3xl shadow-xl p-12 md:p-16 text-center">
          <Loader className="w-12 h-12 text-[color:var(--pmc-accent)] animate-spin mx-auto mb-4" />
          <p className="text-xl text-gray-600 font-inter">Verifying reset code...</p>
        </div>
      </div>
    );
  }

  if (!isValidCode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-3xl shadow-xl p-12 md:p-16">
          <div className="flex flex-col items-center mb-8">
            <div className="mb-6">
              <img 
                src="/landing/getpawtected.svg" 
                alt="GetPawtected" 
                className="w-64 md:w-80 lg:w-96 h-auto"
              />
            </div>
            <h1 className="text-4xl md:text-3xl font-medium font-poppins text-black mb-4 text-center">
              Invalid Reset Link
            </h1>
          </div>

          <div className="mb-6 p-6 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-base text-red-700 font-inter">{error}</p>
          </div>

          <div className="space-y-4">
            <Link 
              to="/forgot-password"
              className="block w-full py-5 px-8 bg-[color:var(--pmc-accent)] text-white rounded-full hover:bg-[color:var(--pmc-accent-hover)] transition-all duration-200 text-center font-poppins text-lg font-medium"
            >
              Request New Reset Link
            </Link>
            <Link 
              to="/login"
              className="block w-full py-5 px-8 bg-white border-2 border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-all duration-200 text-center font-poppins text-lg font-medium"
            >
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-3xl shadow-xl p-12 md:p-16">
        <div className="flex flex-col items-center mb-8">
          <div className="mb-6">
            <img 
              src="/landing/getpawtected.svg" 
              alt="GetPawtected" 
              className="w-64 md:w-80 lg:w-96 h-auto"
            />
          </div>
          <h1 className="text-4xl md:text-3xl font-medium font-poppins text-black mb-4 text-center">
            Reset Your Password
          </h1>
          
        </div>

        {error && (
          <div className="mb-6 p-6 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-base text-red-700 font-inter">{error}</p>
          </div>
        )}

        {success ? (
          <div className="mb-6 p-6 bg-green-50 border border-green-200 rounded-2xl flex items-start gap-4">
            <CheckCircle className="w-6 h-6 text-green-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-base text-green-700 font-inter font-medium mb-2">
                Password Reset Successful!
              </p>
              <p className="text-sm text-green-600 font-inter">
                Your password has been updated. Redirecting you to the login page...
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleResetPassword} className="space-y-6">
            {/* New Password Input */}
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2 font-inter">
                New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  id="newPassword"
                  name="newPassword"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setError('');
                  }}
                  className="w-full pl-12 pr-12 py-4 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-[color:var(--pmc-accent)] transition-colors font-inter text-base"
                  placeholder="Enter new password (min. 8 characters)"
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Confirm Password Input */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2 font-inter">
                Confirm New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setError('');
                  }}
                  className="w-full pl-12 pr-12 py-4 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-[color:var(--pmc-accent)] transition-colors font-inter text-base"
                  placeholder="Confirm your new password"
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-5 px-8 bg-[color:var(--pmc-accent)] text-white rounded-full hover:bg-[color:var(--pmc-accent-hover)] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 font-poppins text-lg font-medium"
            >
              {loading ? (
                <>
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Resetting Password...</span>
                </>
              ) : (
                <span>Reset Password</span>
              )}
            </button>
          </form>
        )}

        {!success && (
          <div className="mt-8 text-center">
            <Link 
              to="/login" 
              className="inline-flex items-center gap-2 text-base text-[color:var(--pmc-accent)] hover:underline font-inter font-medium"
            >
              Back to Sign In
            </Link>
          </div>
        )}

        <div className="mt-6 text-center">
          <p className="text-gray-400 font-inter text-sm">
            Pet Registration & Insurance Management System
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;

